/**
 * Leitura do campo `note` das Observations.
 *
 * O app envia um item por registro de despertar, por exemplo:
 *   note: [
 *     { text: "Registro de despertar 1: 01:39 -> 01:45" },
 *     { text: "Registro de despertar 2: 06:01 -> 08:45" },
 *   ]
 *
 * Despertar e nota do paciente são coisas diferentes: o despertar vai para o
 * fim da seção Sono e a nota do paciente vai para a seção própria, no fim da
 * página. Por isso a separação acontece em um único lugar, aqui.
 */

import dayjs from "dayjs";

/** Um despertar já formatado para exibição. */
export interface AwakeningRecord {
  id: string;
  /** Ex.: "Despertar 1" */
  label: string;
  /** Ex.: "01:39 → 01:45" */
  value: string;
}

/** "Registro de despertar 1: 01:39 -> 01:45" (o número é opcional). */
const AWAKENING_LINE = /^registros?\s+de\s+despertar\s*(\d*)\s*:\s*(.+)$/i;

/** Textos de note[], já quebrados por linha e sem espaços sobrando. */
function getNoteLines(resource: any): string[] {
  const notes = Array.isArray(resource?.note) ? resource.note : [];

  return notes
    .map((note: any) => (typeof note === "string" ? note : note?.text))
    .filter((text: any): text is string => typeof text === "string")
    .flatMap((text: string) => text.split("\n"))
    .map((line: string) => line.trim())
    .filter(Boolean);
}

export function isAwakeningNoteLine(text: unknown): boolean {
  return typeof text === "string" && AWAKENING_LINE.test(text.trim());
}

/** "01:39 -> 01:45" vira "01:39 → 01:45". */
function formatAwakeningValue(text: string): string {
  return text
    .replace(/\s*(?:-+>|–>|→)\s*/g, " → ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Todos os despertares do período, sem repetir o mesmo horário.
 * Repetições acontecem quando o aparelho envia o mesmo sono em dois syncs.
 */
export function collectAwakeningRecords(entries: any[]): AwakeningRecord[] {
  const seen = new Set<string>();
  const records: AwakeningRecord[] = [];

  entries.forEach((entry: any) => {
    getNoteLines(entry?.resource).forEach((line) => {
      const match = line.match(AWAKENING_LINE);
      if (!match) return;

      const value = formatAwakeningValue(match[2]);
      if (!value || seen.has(value)) return;
      seen.add(value);

      const position = records.length + 1;
      records.push({
        id: `awakening-${position}`,
        // Sem número no texto original, numeramos pela ordem de chegada
        label: `Despertar ${match[1] || position}`,
        value,
      });
    });
  });

  return records;
}

/** Nota do paciente de UMA Observation (sem as linhas de despertar). */
function getPatientNoteText(resource: any): string {
  const notes = Array.isArray(resource?.note) ? resource.note : [];

  return notes
    .map((note: any) => (typeof note === "string" ? note : note?.text))
    .filter((text: any): text is string => typeof text === "string")
    .map((text: string) =>
      text
        .split("\n")
        .filter((line) => !isAwakeningNoteLine(line))
        .join("\n")
        .trim()
    )
    .filter(Boolean)
    .join("\n\n");
}

/** Momento do envio, usado para saber qual nota é a mais recente. */
function issuedTime(resource: any): number {
  const raw = resource?.issued ?? resource?.meta?.lastUpdated;
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.valueOf() : 0;
}

/**
 * Nota mais recente entre as entries recebidas.
 * Em "Todos" recebe o dia inteiro (mostra a última que o paciente enviou);
 * com um horário selecionado recebe só aquele envio (mostra a nota dele).
 * Notas antigas não são concatenadas: cada envio substitui a anterior.
 */
export function getLatestPatientNoteText(entries: any[]): string | undefined {
  let latestText: string | undefined;
  let latestTime = -1;

  entries.forEach((entry: any) => {
    const resource = entry?.resource;
    const text = getPatientNoteText(resource);
    if (!text) return;

    const time = issuedTime(resource);
    if (time < latestTime) return;

    latestTime = time;
    latestText = text;
  });

  return latestText;
}
